import React, { useMemo, useState, useEffect } from 'react';
import clsx from 'clsx';
import { BadgeRating } from '../Badge/Badge';
import {
  type CardCountryProps,
  getCountryVerdict,
  getCountryStatusText,
} from './CardCountry.types';
import { Verdict } from '@/helpers/ratingState';

/**
 * Mapowanie kodów krajów do dynamicznych importów flag (lazy loading)
 */
const flagImports: Record<string, () => Promise<{ default: string }>> = {
  ua: () => import('./assets/ua.png'),
  si: () => import('./assets/si.png'),
  ro: () => import('./assets/ro.png'),
  sk: () => import('./assets/sk.png'),
  dk: () => import('./assets/dk.png'),
  cz: () => import('./assets/cz.png'),
  ca: () => import('./assets/ca.png'),
  it: () => import('./assets/it.png'),
  hu: () => import('./assets/hu.png'),
};

/**
 * Hook do lazy loading flagi kraju
 * Ładuje flagę tylko gdy jest potrzebna
 */
const useFlag = (countryCode: string): string | undefined => {
  const [flagUrl, setFlagUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const code = countryCode.toLowerCase();
    const importFlag = flagImports[code];

    if (importFlag) {
      importFlag()
        .then((module) => {
          setFlagUrl(module.default);
        })
        .catch((error) => {
          console.warn(
            `Nie udało się załadować flagi dla kraju: ${countryCode}`,
            error,
          );
        });
    }
  }, [countryCode]);

  return flagUrl;
};

/**
 * Komponent karty kraju z informacją o statusie sprawdzenia
 *
 * @example
 * ```tsx
 * <CardCountry
 *   country={{ code: 'CA', name: 'Kanada' }}
 *   status={CountryCheckStatus.CLEAR}
 *   title="Ewidencja pojazdów skradzionych"
 * />
 * ```
 */
export const CardCountry: React.FC<CardCountryProps> = ({
  country,
  status,
  recommendation,
  className = '',
  i18n,
}) => {
  const verdict = useMemo(() => getCountryVerdict(status), [status]);
  const displayText = useMemo(
    () => getCountryStatusText(status, i18n),
    [status, i18n],
  );

  // Lazy loading flagi jeśli nie została podana w country.flagUrl
  const lazyFlagUrl = useFlag(country.flagUrl ?? country.code);

  // i18n fallbacks
  const recommendationLabel = i18n?.recommendationLabel || 'Rekomendacja:';
  const flagAltTemplate = i18n?.flagAltTemplate || 'Flaga {country}';
  const flagAlt = flagAltTemplate.replace('{country}', country.name);

  return (
    <div
      className={clsx(
        'bg-white rounded-2xl border',
        'border-gray-300 shadow-sm',
        'overflow-hidden w-full',
        className,
      )}
    >
      <div className="flex flex-col gap-8 px-5 py-7">
        {/* Sekcja górna: Kraj + Badge */}
        <div className="flex gap-8 justify-between items-center">
          {/* Lewa strona: Ikona + Nazwa kraju */}
          <div className="flex gap-6 items-center">
            {/* Ikona flagi */}
            <div className="flex relative justify-center items-center w-10 h-10 rounded-full border border-gray-100 border-solid border-1">
              {lazyFlagUrl ? (
                <img
                  src={lazyFlagUrl}
                  alt={flagAlt}
                  className="object-cover w-full rounded-full"
                />
              ) : (
                <div className="flex justify-center items-center w-8 h-8 text-xs font-bold text-gray-600 bg-gray-200 rounded-full">
                  {country.code}
                </div>
              )}
            </div>

            {/* Nazwa kraju */}
            <h3 className="text-base font-bold text-text-primary xl:text-xl">
              {country.name}
            </h3>
          </div>

          {/* Prawa strona: Badge */}
          <div className="flex flex-col gap-2 items-end">
            <BadgeRating
              rating={verdict}
              version="v2"
              badgeText={displayText}
            />
          </div>
        </div>
        <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-2.5">
          <p className="flex-grow-0 flex-shrink-0 text-sm text-left text-[#20262a]">
            {i18n?.comment}
          </p>
        </div>
      </div>
      {verdict === Verdict.NEGATIVE && recommendation && (
        <div className="px-5 py-4 bg-red-800">
          <p className="font-inter text-sm leading-[1.57] text-white">
            <strong className="font-extrabold">{recommendationLabel}</strong>{' '}
            {recommendation}
          </p>
        </div>
      )}
    </div>
  );
};
